import { useTabsStore, type Tab } from '@/shared/stores/tabs.store'
import { useMessagesStore } from '@/shared/stores/messages.store'

export function TabBar() {
  const tabs = useTabsStore(s => s.tabs)
  const activeTabId = useTabsStore(s => s.activeTabId)
  const setActive = useTabsStore(s => s.setActive)
  const removeTab = useTabsStore(s => s.removeTab)

  if (tabs.length === 0) return null

  return (
    <div
      className="tabbar"
      style={{
        display: 'flex',
        alignItems: 'stretch',
        overflowX: 'auto',
        background: 'var(--color-bg-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '0 var(--space-2)',
        gap: 'var(--space-1)',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none' as React.CSSProperties['msOverflowStyle'],
        flexShrink: 0,
        minHeight: 38,
      }}
    >
      {tabs.map(tab => (
        <TabItem
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeTabId}
          onActivate={() => setActive(tab.id)}
          onClose={() => removeTab(tab.id)}
        />
      ))}
    </div>
  )
}

function TabItem({
  tab, isActive, onActivate, onClose,
}: {
  tab: Tab
  isActive: boolean
  onActivate: () => void
  onClose: () => void
}) {
  const isStreaming = useMessagesStore(s =>
    tab.sessionId ? (s.sessions[tab.sessionId]?.isStreaming ?? false) : false
  )

  return (
    <div
      onClick={onActivate}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: 'var(--space-2) var(--space-3)',
        minWidth: 120,
        maxWidth: 200,
        cursor: 'pointer',
        background: isActive ? 'var(--color-bg-base)' : 'transparent',
        borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
        borderBottom: isActive
          ? '2px solid var(--color-accent)'
          : '2px solid transparent',
        flexShrink: 0,
        transition: 'background var(--duration-fast)',
      }}
    >
      {/* Type icon */}
      <span style={{
        fontSize: 12,
        color: 'var(--color-text-muted)',
        flexShrink: 0,
      }}>
        {tab.type === 'conversation' ? '\u25CE' : '\u25A1'}
      </span>

      {/* Title */}
      <span style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-xs)',
        color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
        fontWeight: isActive ? 500 : 400,
      }}>
        {tab.title}
      </span>

      {/* Streaming indicator */}
      {isStreaming && (
        <span style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--color-accent)',
          flexShrink: 0,
          animation: 'pulse 1.5s infinite',
        }} />
      )}

      {/* Close button */}
      <button
        onClick={(e) => { e.stopPropagation(); onClose() }}
        onPointerDown={(e) => e.stopPropagation()}
        className="tab-close-btn"
        style={{
          background: 'none',
          border: 'none',
          padding: '4px 6px',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          fontSize: 16,
          lineHeight: 1,
          flexShrink: 0,
          borderRadius: 3,
          opacity: 0.8,
          minWidth: 28,
          minHeight: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Fermer l'onglet"
      >
        {'\u00D7'}
      </button>
    </div>
  )
}

